import React, { useEffect, useRef } from "react";
import { TooltipInterface } from '@att-bit/duc.components.tooltip';
import { AnalyticsEvent } from '@att-bit/ui-frameworks.components.analytics-event';
import { docComplete } from '@att-bit/duc.utils.types';
import { SectionTheme } from "@att-bit/duc.utils-ts.types";
import {
  Orientation,
  useWindowSize,
  Viewport,
} from "@att-bit/duc.hooks.use-window-size";
import {
  LegalWrapper,
  LegalWrapperInterface,
} from "@att-bit/duc.components.legal-wrapper";
// Fixing missing file issue;

export type TextProps = {
  /** parse children for HTML */
  allowDangerousHtml?: boolean;
  /** a text to be rendered in the component.  */
  children: React.ReactNode;
  /** sizes XXXL | XXL | XL | LG | MD  */
  size?: TextSizes;
  /** wrapper tag 'div' | 'p' | 'span' */
  wrapper?: TextWrappers;
  /** color classes from global styles */
  color?: string;
  /** font classes from global styles */
  font?: TextFonts;
  /** line height class */
  lineHeight?: TextLineHeights;
  /** runtime theme injection */
  themetext?: TextTheme;
  /** list icon class */
  bulletType?: string;
  /** spacing class */
  margin?: string[];
  /** Entry to capture legal data */
  legalData?: any;
  /** to send additional data for link click inside text component */
  linkAnalyticsObj?: any;
  /** Enabled to send additional data for link click inside text component by default false */
  enableLinkAnalytics?: boolean;
  /** data for tooltip */
  tooltipData?: any;
};

export enum TextLineHeights {
  LOOSE = "line-h-loose",
  NORMAL = "line-h-normal",
  TIGHT = "line-h-tight",
}

export enum TextFonts {
  REGULAR = "font-regular",
  ITALIC = "font-italic",
  BOLD = "font-bold",
  ITALIC_BOLD = "font-bold-italic",
}

export enum TextWrappers {
  DIV = "div",
  P = "p",
  SPAN = "span",
  // FRAGMENT = 'Fragment',
}

export interface TextTheme {
  color: string;
  size: string;
  margin: string[];
  sectiontheme: SectionTheme | string;
  viewport: Viewport;
}

export enum TextSizes {
  LG = "type-lg",
  BASE = "type-base",
  BASEWITHPADDING = "type-base pad-xs",
  SM = "type-sm",
  XS = "type-xs",
  XXS = "type-xxs",
}

export function Text({
  allowDangerousHtml = false,
  children,
  size = TextSizes.BASE,
  wrapper = TextWrappers.DIV,
  color,
  font,
  lineHeight,
  themetext,
  bulletType = "bullet",
  margin,
  legalData,
  linkAnalyticsObj,
  enableLinkAnalytics=false,
  tooltipData,
}: TextProps) {
  const orientation = useWindowSize()?.orientation;
  const Tag: any = wrapper;
  const textRef = useRef(null);
  const classes = [];
  size ? classes.push(size) : themetext?.size && classes.push(themetext.size);
  font && classes.push(font);
  margin
    ? classes.push(...margin)
    : themetext?.margin && classes.push(...themetext.margin);
  lineHeight && classes.push(lineHeight);
  color
    ? classes.push(color)
    : themetext?.color && classes.push(themetext.color);
  classes.push('rte-styles');
  let legalObj, tooltipObj, content;
  if (legalData) {
    legalObj = LegalWrapperInterface.initWithSanity("lgl", { linkAnalyticsData: linkAnalyticsObj, ...legalData });
  }
  if (tooltipData) {
    tooltipObj = TooltipInterface.initWithSanity("ttp", tooltipData);
    content = children.toString().replace('<p>', '').replace('</p>', '');
  }
  if (children) {   
    if(children.toString().includes("<ul") && (bulletType && bulletType !== "bullet")){
      let addWrapperDiv;
      if(bulletType === 'checkmark'){
        addWrapperDiv = `<div class='list-svg line-h-normal ${size} rte-${bulletType}-default'><ul`;
      }else {
        addWrapperDiv = `<div class='list-svg line-h-normal ${size} rte-${bulletType}'><ul`;
      }   
      children = children.toString().replaceAll("<ul", addWrapperDiv).replaceAll("</ul>", "</ul></div>").replaceAll("<li>","<li><span>").replaceAll("</li>","</span></li>");
    }  
    if(children.toString().includes("<ul") && bulletType && bulletType === "bullet"){
      const addWrapperDiv = `<div class='list line-h-normal ${size}'><ul`;
      children = children.toString().replaceAll("<ul", addWrapperDiv).replaceAll("</ul>", "</ul></div>");
    } 
    if(children.toString().includes("<ol")) {
      const addWrapperDiv = `<div class='list line-h-normal ${size}'><ol`;
      children = children.toString().replaceAll("<ol", addWrapperDiv).replaceAll("</ol>", "</ol></div>");
    }
  } else {
    return(null);
  }
  //classes.push(...textListClass);
  useEffect(() => {
    const linkEleArr = textRef?.current?.querySelectorAll("a");
    if (enableLinkAnalytics) {
      linkEleArr?.forEach((linkItem: any) => {
        linkItem.addEventListener("click", handleClick);
      });
    }
  }, []);

  const handleClick = (e: any) => {
    const lnkname = e.target?.innerText;
    const destUrl = (e.target?.nodeName === 'A') ? e.target?.href : ((e.target?.parentElement?.nodeName === 'A') ? e.target?.parentElement?.href : '');
    const lnkPos = 'Body';
    const data = {
        linkDestinationUrl: destUrl,
        linkName: lnkname,
        linkPosition: lnkPos,
        ...linkAnalyticsObj
    };
    const lnkClickObj = {
      "eventCode": "Link_Click",
      "eventAction": "linkClick",
      "additionalData": data
    };
    docComplete(()=>{
      AnalyticsEvent(lnkClickObj);
    });
  };

  return (
    <>
      {children ? allowDangerousHtml ? (
        <Tag
          className={classes.join(" ")}
          dangerouslySetInnerHTML={{ __html: children }}
          ref={textRef}
        />
      ) : (
        <Tag className={classes.join(" ")} ref={textRef}>
          {tooltipData ? (<><span dangerouslySetInnerHTML={{ __html: content }}/>{tooltipObj.toJsx()}</>) : children}
        </Tag>
      ):''}
      {legalObj && legalObj.data && (
        <div className="mar-t-xs">
          <LegalWrapper
            data={{ ...legalObj.data }}
            options={{ ...legalObj.options, id: legalObj.options.id }}
          />
        </div>
      )}</>
  );
}